// Feature Documentation: docs/features/pilot-user-workflow.md
import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

import {
  Box, Text, Button, Grid, GridItem, Textarea, VStack, Heading,
  useToast, Tabs, TabList, TabPanels, Tab, TabPanel,
  Badge, HStack, Divider, Table, Thead, Tbody, Tr, Th, Td,
  Tag, TagLabel, Flex, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
  ModalBody, ModalCloseButton, useDisclosure, Icon, Card, CardHeader, CardBody, Spinner,
  Collapse, IconButton, Select, Input, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  FormControl, FormLabel, Checkbox, Progress,
} from "@chakra-ui/react";
import { AddIcon, AttachmentIcon, EditIcon, CheckCircleIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, DownloadIcon, StarIcon, DeleteIcon, ExternalLinkIcon, InfoIcon, ViewIcon } from '@chakra-ui/icons';

import { callApi } from '../callApi';
import { AppContext } from './AppContext';

import HeaderMenus from './HeaderMenus';
import ModernChat from './ModernChat';
import { useLocation } from 'react-router-dom';

// Feedback Component (unchanged from original)
function FeedbackComponent({ resultsId, existingFeedback, onFeedbackSubmitted, accessToken }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const hasSubmittedFeedback = existingFeedback && existingFeedback.length > 0;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: 'Rating required',
        description: 'Please select a star rating before submitting.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await callApi('/api/feedback/coding', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coding_results_id: resultsId,
          rating: rating,
          feedback_text: feedbackText,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Feedback submitted',
          description: 'Thank you for your feedback!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onFeedbackSubmitted();
      } else {
        throw new Error(data.error || 'Failed to submit feedback');
      }
    } catch (error) {
      toast({
        title: 'Submission failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (hasSubmittedFeedback) {
    return (
      <Box mt={6} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
        <Heading size="sm" mb={3}>Feedback Submitted</Heading>
        {existingFeedback.map((fb, index) => (
          <Box key={index} mb={2}>
            <HStack>
              {[...Array(5)].map((_, i) => (
                <Icon as={StarIcon} key={i} color={i < fb.rating ? "gold" : "gray.300"} />
              ))}
            </HStack>
            <Text fontSize="sm" mt={1}>"{fb.feedback_text}"</Text>
            <Text fontSize="xs" color="gray.500">- {fb.user.name} on {new Date(fb.created_at).toLocaleDateString()}</Text>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box mt={6} p={4} borderWidth="1px" borderRadius="md" bg="blue.50">
      <Heading size="sm" mb={3}>Rate these results</Heading>
      <HStack spacing={2} mb={3}>
        {[...Array(5)].map((_, index) => {
          const ratingValue = index + 1;
          return (
            <Icon
              key={ratingValue}
              as={StarIcon}
              boxSize={6}
              color={ratingValue <= (hover || rating) ? "gold" : "gray.300"}
              onClick={() => setRating(ratingValue)}
              onMouseEnter={() => setHover(ratingValue)}
              onMouseLeave={() => setHover(0)}
              cursor="pointer"
              transition="color 0.2s"
            />
          );
        })}
      </HStack>
      <Textarea
        placeholder="Optional: Provide additional feedback or suggest corrections..."
        value={feedbackText}
        onChange={(e) => setFeedbackText(e.target.value)}
        mb={3}
        size="sm"
      />
      <Button
        colorScheme="blue"
        size="sm"
        onClick={handleSubmit}
        isLoading={isSubmitting}
        isDisabled={rating === 0}
      >
        Submit Feedback
      </Button>
    </Box>
  );
}

// Component to display answered clarification questions
function AnsweredQuestionsDisplay({ questions }) {
  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <Box mb={6} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
      <Heading size="sm" mb={3}>Clarification History</Heading>
      <VStack spacing={4} align="stretch">
        {questions.map(q => (
          <Box key={q.id}>
            <Text fontWeight="bold" fontSize="sm">{q.question_text}</Text>
            <Text fontSize="sm" color="gray.700" pl={2}>↳ {q.answer_text}</Text>
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

function PilotUploader() {
  // SIMPLIFIED STATE - Single Source of Truth
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    start: 0,
    limit: 20,
    total: 0,
    hasMore: true
  });
  
  // Form state
  const [textContent, setTextContent] = useState('');
  const [showContentEntry, setShowContentEntry] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [questionsOpen, setQuestionsOpen] = useState(false);
  
  // Clarification questions state
  const [answers, setAnswers] = useState({});
  const [isSubmittingAnswers, setIsSubmittingAnswers] = useState(false);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  
  // Configuration state
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPayer, setSelectedPayer] = useState('');
  const [selectedState, setSelectedState] = useState('');
  
  // Assign Payer tab state
  const [assignPayer, setAssignPayer] = useState('BCBS');
  const [assignState, setAssignState] = useState('Texas');
  const [isAssigningPayer, setIsAssigningPayer] = useState(false);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  
  // Final CPT tab state
  const [isApplyingCarrierRules, setIsApplyingCarrierRules] = useState(false);
  const [carrierAdjustmentResults, setCarrierAdjustmentResults] = useState(null);
  const [includeNcci, setIncludeNcci] = useState(true); // NCCI validation checkbox
  
  // Available options
  const [availableClients, setAvailableClients] = useState([]);
  const payerOptions = ['BCBS', 'UHC', 'Aetna', 'CIGNA', 'Medicare'];
  const stateOptions = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  // Modal state for file content
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const { accessToken, user } = useContext(AppContext);
  const toast = useToast();
  const location = useLocation();
  const hasLoadedFromUrl = useRef(false);
  const pollingIntervalRef = useRef(null);

  // SINGLE SOURCE OF TRUTH: One function to fetch all case data
  const fetchCaseData = async (caseId) => {
    if (!caseId) return null;
    
    try {
      const response = await callApi(`/get_case_details/${caseId}`, accessToken);
      const data = await response.json();
      
      if (response.ok) {
        return data;
      } else {
        toast({
          title: 'Error fetching case details',
          description: data.error || 'Failed to fetch case details',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return null;
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return null;
    }
  };

  // Fetch workflow status to check payer assignment
  const fetchWorkflowStatus = async (caseId) => {
    if (!caseId) return null;
    
    try {
      console.log('Fetching workflow status for case:', caseId);
      const response = await callApi(`/workflow_status/${caseId}`, accessToken);
      const data = await response.json();
      
      if (response.ok) {
        console.log('Workflow status response:', data);
        return data;
      } else {
        // Don't show error toast for workflow status as it's supplementary data
        console.warn('Failed to fetch workflow status:', data.error);
        return null;
      }
    } catch (error) {
      console.warn('Error fetching workflow status:', error.message);
      return null;
    }
  };

  // SELECTIVE REFRESH: Only update fields that can change during processing
  const refreshCurrentCase = async (isSilent = false) => {
    if (!currentCaseId) return;
    
    if (!isSilent) setLoading(true);
    try {
      const freshData = await fetchCaseData(currentCaseId);
      
      // For workflow status, we need to use external_case_id, not internal id
      let freshWorkflowStatus = null;
      if (freshData?.external_case_id) {
        freshWorkflowStatus = await fetchWorkflowStatus(freshData.external_case_id);
      }

      const dataArray = [freshData, freshWorkflowStatus];
      
      if (dataArray[0]) {
        const freshData = dataArray[0];
        const freshWorkflowStatus = dataArray[1];
        // Smart Refresh Logic
        setCaseData(prevData => {
          // If there's no previous data, or if the case ID has changed,
          // it's a case switch or initial load, so perform a full refresh.
          if (!prevData || prevData.id !== freshData.id) {
            return freshData;
          }

          // Otherwise, it's a poll for the same case. Perform a selective refresh
          // to avoid overwriting user input or causing UI flashes.
          return {
            ...prevData,
            // Dynamic fields that change during processing
            cpt_status: freshData.cpt_status,
            icd10_status: freshData.icd10_status,
            cpt_coding_results: freshData.cpt_coding_results,
            icd10_coding_results: freshData.icd10_coding_results,
            cpt_coding_results_id: freshData.cpt_coding_results_id,
            icd10_coding_results_id: freshData.icd10_coding_results_id,
            cpt_feedback: freshData.cpt_feedback,
            icd10_feedback: freshData.icd10_feedback,
            case_summary: freshData.case_summary,
            coder_notes: freshData.coder_notes,
            coder_score: freshData.coder_score,
            coder_instructions: freshData.coder_instructions,
            
            // Also update fields that might change during processing (e.g., after submitting answers)
            files: freshData.files,
            clarification_questions: freshData.clarification_questions,
            answered_clarification_questions: freshData.answered_clarification_questions,
          };
        });
        
        // Auto-collapse content entry if we have CPT results
        if (freshData.cpt_coding_results) {
          setShowContentEntry(false);
        }
      }

      // Update workflow status separately
        if (freshWorkflowStatus) {
          console.log('Setting workflow status:', freshWorkflowStatus);
          setWorkflowStatus(freshWorkflowStatus);
        } else {
          console.log('No workflow status returned');
        }
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  // SIMPLIFIED CASES FETCH
  const fetchCases = async (loadMore = false, searchFilter = activeSearch) => {
    setCasesLoading(true);
    try {
      const start = loadMore ? paginationInfo.start + paginationInfo.limit : 0;
      
      let url = `/list_external_cases?start=${start}&limit=${paginationInfo.limit}`;
      if (searchFilter) {
        url += `&external_case_id_search=${encodeURIComponent(searchFilter)}`;
      }

      const response = await callApi(url, accessToken);
      const data = await response.json();
      
      if (response.ok) {
        const casesData = data.cases || [];
        const filteredCases = casesData.filter(caseItem => caseItem.encounter_id);
        const sortedCases = filteredCases.sort((a, b) => {
          return b.date_updated.localeCompare(a.date_updated);
        });
        
        if (loadMore) {
          setCases(prevCases => [...prevCases, ...sortedCases]);
        } else {
          setCases(sortedCases);
        }
        
        if (data.pagination) {
          setPaginationInfo({
            start: data.pagination.start,
            limit: data.pagination.limit,
            total: data.pagination.total,
            hasMore: data.pagination.has_more
          });
        }
      } else {
        toast({
          title: 'Error fetching cases',
          description: data.error || 'Failed to fetch cases',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCasesLoading(false);
    }
  };

  // SIMPLIFIED POLLING: Always refresh when polling is active
  const startPolling = () => {
    if (pollingIntervalRef.current) return; // Already polling
    
    pollingIntervalRef.current = setInterval(() => {
      if (currentCaseId) {
        refreshCurrentCase(true); // Silent refresh
        fetchCases(); // Also refresh the cases list to update statuses
      }
    }, 5000); // Poll every 5 seconds
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Load cases and handle URL parameters
  useEffect(() => {
    fetchCases();
  }, [accessToken]);

  // Handle case ID changes
  useEffect(() => {
    if (currentCaseId) {
      refreshCurrentCase();
    } else {
      setCaseData(null);
      setShowContentEntry(true);
    }
  }, [currentCaseId]);

  // Start/stop polling based on case status
  useEffect(() => {
    const isProcessing = caseData?.cpt_status === 'in_progress' || caseData?.icd10_status === 'in_progress';

    if (isProcessing) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling(); // Cleanup on component unmount or when caseData is cleared
  }, [caseData?.cpt_status, caseData?.icd10_status]);

  // Handle URL loading
  useEffect(() => {
    if (accessToken && !hasLoadedFromUrl.current) {
      const params = new URLSearchParams(location.search);
      const caseIdFromUrl = params.get('caseId');
      
      if (caseIdFromUrl) {
        setCurrentCaseId(caseIdFromUrl);
        hasLoadedFromUrl.current = true;
      }
    }
  }, [accessToken, location.search]);

  // Load clients
  useEffect(() => {
    const fetchClients = async () => {
      if (!accessToken) return;
      
      try {
        const response = await callApi('/clients', accessToken);
        const data = await response.json();
        
        if (response.ok && data.clients) {
          const activeClients = data.clients.filter(client => client.active);
          setAvailableClients(activeClients);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };
    
    fetchClients();
  }, [accessToken]);

  // Set default client based on API response
  useEffect(() => {
    if (availableClients.length > 0) {
      const defaultClient = availableClients.find(c => c.is_default);
      if (defaultClient) {
        setSelectedClient(defaultClient);
      }
    }
  }, [availableClients]);

  const CATEGORY_COLORS = {
    'General': 'green',
    'PayerResponse': 'orange',
    'Operative Report': 'purple',
  };

  const getCategoryColor = (category) => CATEGORY_COLORS[category] || 'gray';

  const sortedFiles = useMemo(() => {
    return (caseData?.files || []).sort((a, b) => {
      const catA = a.document_category || 'zzz';
      const catB = b.document_category || 'zzz';
      if (catA !== catB) {
        return catA.localeCompare(catB);
      }
      return (a.file_name || '').localeCompare(b.file_name || '');
    });
  }, [caseData?.files]);

  // SIMPLIFIED ACTION HANDLERS
  const createCase = async () => {
    try {
      const randomCaseId = Math.random().toString(36).substr(2, 9);

      const requestBody = { 
        external_case_id: randomCaseId,
        case_type: null,
      };
      
      if (selectedClient) {
        requestBody.client_id = selectedClient.id;
      }
      
      const response = await callApi('/create_external_case', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Case created',
          description: `Working with new case ID: ${data.case_id}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Assign carrier if needed
        if (selectedPayer || selectedState) {
          try {
            await callApi('/assign_carrier', accessToken, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                case_id: randomCaseId,
                payer_type: selectedPayer,
                carrier_name: selectedPayer,
                state_full: selectedState
              }),
            });
          } catch (carrierError) {
            console.warn('Carrier assignment failed:', carrierError);
          }
        }
        
        await fetchCases();
        return data.case_id;
      } else {
        throw new Error(data.error || 'Failed to create case');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return null;
    }
  };

  const selectCase = async (caseId) => {
    setCurrentCaseId(caseId);
    setActiveTab(0);
    
    toast({
      title: 'Case selected',
      description: `Working with case ID: ${caseId}`,
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleNewCase = () => {
    setCurrentCaseId(null);
    setTextContent('');
    setActiveTab(0);
    
    toast({
      title: 'New Case',
      description: 'Ready to create a new case',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const submitContent = async (caseId, formData, endpoint) => {
    const response = await callApi(endpoint, accessToken, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();
    if (response.ok) {
      toast({
        title: 'Content uploaded',
        description: 'Your content was successfully uploaded',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh everything
      await fetchCases();
      await refreshCurrentCase();
      
      return data;
    } else {
      throw new Error(data.error || 'Failed to upload content');
    }
  };

  const handleTextSubmit = async () => {
    if (!textContent.trim()) {
      toast({
        title: 'Empty content',
        description: 'Please enter some text before submitting',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      let caseIdToUse = currentCaseId;
      
      if (!caseIdToUse) {
        caseIdToUse = await createCase();
        if (!caseIdToUse) {
          throw new Error('Failed to create a new case');
        }
        setCurrentCaseId(caseIdToUse);
      }

      const formData = new FormData();
      formData.append('file_type_id', '5');
      const blob = new Blob([textContent], { type: 'text/plain' });
      formData.append('file', blob, 'content.txt');

      await submitContent(caseIdToUse, formData, `/upload_content/${caseIdToUse}`);
      setTextContent('');
    } catch (error) {
      toast({
        title: 'Content submission failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setLoading(true);
      try {
        let caseIdToUse = currentCaseId;
        if (!caseIdToUse) {
          caseIdToUse = await createCase();
          if (!caseIdToUse) {
            throw new Error('Failed to create a new case');
          }
          setCurrentCaseId(caseIdToUse);
        }
        
        const formData = new FormData();
        formData.append('file_type_id', '5');
        formData.append('file', file);

        await submitContent(caseIdToUse, formData, `/upload_file/${caseIdToUse}`);
      } catch (error) {
        toast({
          title: 'File upload failed',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fileInput.click();
  };

  const performCptCoding = async () => {
    if (!currentCaseId) {
      toast({
        title: 'No case selected',
        description: 'Please add content first to create a case',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        case_id: currentCaseId,
        case_type: null,
        background: true
      };

      // Smart Button Logic: Add context flag if answered questions exist
      if (caseData && caseData.answered_clarification_questions && 
          caseData.answered_clarification_questions.length > 0) {
        payload.use_clarification_context = true;
      }

      const response = await callApi(`/cpt_coding`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'CPT Coding Started',
          description: 'Processing will complete in the background',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        
        // Start polling for updates
        startPolling();
        
        // Refresh immediately to show updated status
        await refreshCurrentCase();
        await fetchCases();
      } else {
        throw new Error(data.error || 'Failed to perform CPT coding');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const performIcd10Coding = async () => {
    if (!currentCaseId) {
      toast({
        title: 'No case selected',
        description: 'Please add content first to create a case',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await callApi(`/icd10_coding`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          case_id: currentCaseId,
          background: true
        }),
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'ICD-10 Coding Started',
          description: 'Processing will complete in the background',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        
        setActiveTab(1);
        startPolling();
        
        await refreshCurrentCase();
        await fetchCases();
      } else {
        throw new Error(data.error || 'Failed to perform ICD-10 coding');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    if (!currentCaseId) {
      toast({
        title: 'No case selected',
        description: 'Please select a case to generate a summary.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await callApi(`/api/case/${currentCaseId}/generate_summary`, accessToken, {
        method: 'POST',
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Summary Generated',
          description: 'The case summary has been successfully generated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        await refreshCurrentCase();
      } else {
        throw new Error(data.error || 'Failed to generate summary');
      }
    } catch (error) {
      toast({
        title: 'Summary Generation Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const assignPayerToCase = async () => {
    if (!currentCaseId || !caseData?.external_case_id) {
      toast({
        title: 'No case selected',
        description: 'Please select a case with a valid external ID to assign a payer.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsAssigningPayer(true);
    try {
      const response = await callApi('/assign_carrier', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseData.external_case_id, // Use external case ID for workflow actions
          payer_type: assignPayer,
          carrier_name: assignPayer,
          state_full: assignState
        }),
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Payer Assigned',
          description: `Successfully assigned ${assignPayer} in ${assignState}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Refresh both case data and workflow status
        await refreshCurrentCase();
      } else {
        throw new Error(data.error || 'Failed to assign payer');
      }
    } catch (error) {
      toast({
        title: 'Payer Assignment Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAssigningPayer(false);
    }
  };

  const applyCarrierRules = async (forceRefresh = false) => {
    if (!currentCaseId || !caseData?.external_case_id) {
      toast({
        title: 'No case selected',
        description: 'Please select a case to apply carrier rules.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsApplyingCarrierRules(true);
    try {
      const endpoint = includeNcci ? '/apply_carrier_rules_with_ncci' : '/apply_carrier_rules';

      const response = await callApi(endpoint, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseData.external_case_id, // Use external case ID
          force_refresh: forceRefresh
        }),
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Carrier Rules Applied',
          description: `Successfully applied carrier-specific coding adjustments${includeNcci ? ' with NCCI validation' : ''}.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        
        setCarrierAdjustmentResults(data);
        // Refresh workflow status to show completion
        await refreshCurrentCase();
      } else {
        throw new Error(data.error || 'Failed to apply carrier rules');
      }
    } catch (error) {
      toast({
        title: 'Carrier Rules Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsApplyingCarrierRules(false);
    }
  };

  // Submit and Code functions
  const handleSubmitAndCode = async () => {
    if (!textContent.trim()) {
      toast({
        title: 'Empty content',
        description: 'Please enter some text before submitting',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // First submit content
      await handleTextSubmit();
      // Then perform coding
      await performCptCoding();
    } finally {
      setLoading(false);
    }
  };

  const handleUploadAndCode = async () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setLoading(true);
      try {
        // First upload file
        let caseIdToUse = currentCaseId;
        if (!caseIdToUse) {
          caseIdToUse = await createCase();
          if (!caseIdToUse) {
            throw new Error('Failed to create a new case');
          }
          setCurrentCaseId(caseIdToUse);
        }
        
        const formData = new FormData();
        formData.append('file_type_id', '5');
        formData.append('file', file);

        await submitContent(caseIdToUse, formData, `/upload_file/${caseIdToUse}`);
        
        // Then perform CPT coding directly with the case ID we know is correct
        const payload = {
          case_id: caseIdToUse,
          case_type: null,
          background: true
        };

        const response = await callApi(`/cpt_coding`, accessToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (response.ok) {
          toast({
            title: 'CPT Coding Started',
            description: 'Processing will complete in the background',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          
          // Start polling for updates and refresh data
          startPolling();
          await refreshCurrentCase();
          await fetchCases();
        } else {
          throw new Error(data.error || 'Failed to perform CPT coding');
        }
      } catch (error) {
        toast({
          title: 'Process failed',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fileInput.click();
  };

  const handleGeneratePdf = async () => {
    if (!caseData?.external_case_id) return;

    setIsPdfGenerating(true);
    try {
      const response = await callApi(`/v2/cases/${caseData.external_case_id}/generate-report`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: 'standard_cms1500_v1',
          claim_type: 'standard',
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          toast({
            title: 'Please allow pop-ups',
            description: 'Your browser blocked the new tab. Please allow pop-ups for this site to view the PDF.',
            status: 'warning',
            duration: 7000,
            isClosable: true,
          });
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF.');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast({
        title: 'PDF Generation Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsPdfGenerating(false);
    }
  };

  // Helper functions (unchanged from original)
  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    
    const parts = dateString.split(' ');
    if (parts.length !== 2) return dateString;
    
    const datePart = parts[0];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const [year, month, day] = datePart.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    
    if (dateObj.getTime() === today.getTime()) {
      return 'Today';
    } else if (dateObj.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      return `${month}/${day}/${year.toString().substr(2)}`;
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getFileCount = (caseItem) => {
    if (!caseItem || !caseItem.files) return 0;
    return Object.keys(caseItem.files).length;
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    onOpen();
  };

  const handleFileDownload = async (file) => {
    try {
      setLoading(true);
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          case_id: currentCaseId,
          file_id: file.id 
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = file.file_name;
        document.body.appendChild(a);
        a.click();
        
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
        
        toast({
          title: 'File Download Started',
          description: `Downloading ${file.file_name}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to download file');
      }
    } catch (error) {
      toast({
        title: 'Download failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch(searchTerm);
    fetchCases(false, searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveSearch('');
    fetchCases(false, '');
  };

  const handleArchiveCase = async (caseId) => {
    try {
      const response = await callApi(`/api/case/${caseId}/archive`, accessToken, {
        method: 'POST',
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Case archived',
          description: data.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        if (currentCaseId === caseId) {
          handleNewCase();
        }
        
        fetchCases();
      } else {
        throw new Error(data.error || 'Failed to archive case');
      }
    } catch (error) {
      toast({
        title: 'Archive Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const loadMoreCases = () => {
    if (paginationInfo.hasMore && !casesLoading) {
      fetchCases(true);
    }
  };

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    const client = clientId ? availableClients.find(c => c.id === parseInt(clientId)) : null;
    setSelectedClient(client);
  };

  const toggleContentEntry = () => {
    setShowContentEntry(!showContentEntry);
  };

  // Clarification questions handlers
  useEffect(() => {
    const initialAnswers = {};
    if (caseData?.clarification_questions) {
      caseData.clarification_questions.forEach(q => {
        initialAnswers[q.id] = '';
      });
    }
    setAnswers(initialAnswers);
  }, [caseData?.clarification_questions]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmitAnswers = async () => {
    if (!caseData?.clarification_questions) return;
    
    setIsSubmittingAnswers(true);
    try {
      const answersPayload = caseData.clarification_questions.map(q => ({
        question_id: q.id,
        answer_text: answers[q.id] || '',
      }));

      // Basic validation
      if (answersPayload.some(a => !a.answer_text.trim())) {
        toast({
          title: 'All questions must be answered',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        setIsSubmittingAnswers(false);
        return;
      }

      const response = await callApi('/case/submit_clarification_answers', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: currentCaseId,
          answers: answersPayload,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Answers Submitted',
          description: 'Your answers have been saved as a new document. You can now re-code the case.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        refreshCurrentCase(); // Refresh the case data
      } else {
        throw new Error(data.error || 'Failed to submit answers');
      }
    } catch (err) {
      toast({
        title: 'Submission Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingAnswers(false);
    }
  };

  // Result formatting functions (unchanged from original - these are quite long so I'll keep them as-is)
  const formatResults = (results) => {
    if (!results) return null;

    const coderNotes = caseData?.coder_notes;
    const answeredQuestions = caseData?.answered_clarification_questions;

    if (caseData && caseData.case_type === 'IONM' && caseData.ionm_case_id) {
      return (
        <Box textAlign="center" py={8}>
          <Heading size="md" mb={4}>IONM Case Coded Successfully</Heading>
          <Button
            as="a"
            href={`/ionmform?caseid=${caseData.ionm_case_id}`}
            colorScheme="blue"
            size="lg"
            leftIcon={<EditIcon />}
          >
            View Results
          </Button>
        </Box>
      );
    }
  
    return (
      <Box>
        {/* 1. Coder Notes (first) */}
        {coderNotes && (
          <Box mb={5} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
            <Heading size="md" mb={3}>Coder Notes</Heading>
            <Box className="markdown-content">
              <ReactMarkdown>{coderNotes}</ReactMarkdown>
            </Box>
          </Box>
        )}

        {/* 2. Confidence Score (second) */}
        {caseData?.coder_score != null && (
          <Box mb={5} p={4} borderWidth="1px" borderRadius="md" bg="blue.50">
            <Heading size="md" mb={2} color="blue.800">Confidence Score</Heading>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              {caseData.coder_score} / 100
            </Text>
            {caseData.coder_instructions && (
              <Box mt={4}>
                <Heading size="sm" mb={2} color="blue.700">Reviewer Instructions</Heading>
                <Text fontSize="sm" color="blue.900">
                  {caseData.coder_instructions}
                </Text>
              </Box>
            )}
          </Box>
        )}

        {/* 3. Questions Accordion - Pending or Answered (third, closed by default) */}
        {((answeredQuestions && answeredQuestions.length > 0) || (caseData?.clarification_questions && caseData.clarification_questions.length > 0)) && (
          <Box mb={5}>
            <Accordion allowToggle defaultIndex={[]}>
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    {answeredQuestions && answeredQuestions.length > 0 ? (
                      <Heading size="sm">Clarification History ({answeredQuestions.length})</Heading>
                    ) : (
                      <Heading size="sm">Provide additional context ({caseData?.clarification_questions?.length || 0} questions)</Heading>
                    )}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  {answeredQuestions && answeredQuestions.length > 0 ? (
                    // Show answered questions history
                    <VStack spacing={4} align="stretch">
                      {answeredQuestions.map(q => (
                        <Box key={q.id}>
                          <Text fontWeight="bold" fontSize="sm">{q.question_text}</Text>
                          <Text fontSize="sm" color="gray.700" pl={2}>↳ {q.answer_text}</Text>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    // Show pending questions form
                    <Box>
                      <Text mb={4} fontSize="sm" color="gray.700">
                        Our AI needs a bit more information to code this case accurately. Please answer the questions below to improve the results.
                      </Text>
                      <VStack spacing={4} align="stretch">
                        {caseData?.clarification_questions?.map(q => (
                          <Box key={q.id}>
                            <Text fontWeight="bold" mb={2}>{q.question_order}. {q.question_text}</Text>
                            <Textarea
                              value={answers[q.id] || ''}
                              onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                              placeholder="Your answer here..."
                              bg="white"
                            />
                          </Box>
                        ))}
                        <Button
                          colorScheme="green"
                          onClick={handleSubmitAnswers}
                          isLoading={isSubmittingAnswers}
                          isDisabled={!caseData?.clarification_questions || caseData.clarification_questions.length === 0}
                        >
                          Submit Answers
                        </Button>
                      </VStack>
                    </Box>
                  )}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        )}

        {/* 4. CPT Code Details (fourth) */}
        {Object.entries(results).map(([code, details], index, array) => {
          const justification = details.justification || '';
          const isPreliminary = justification.toLowerCase().includes('preliminary');


          return (
          <Box 
            key={code} 
            mb={5} 
            p={isPreliminary ? 4 : 0} 
            borderRadius="md" 
            bg={isPreliminary ? "red.100" : "transparent"}
          >
            <Heading size="md" mb={1}>{code}</Heading>
            <Text fontWeight="bold" mb={3}>{details.long_description}</Text>
            
            <Box pl={4} mb={3}>
              <Text mb={1}>Billable: {details.billable ? "Yes" : "No"}</Text>
              {details.work_rvu !== 0 && <Text mb={1}>Work RVU: {details.work_rvu}</Text>}
              <Text mb={1}>Units: {details.units}</Text>
              <Text mb={1}>Modifiers: {details.modifiers || "none"}</Text>
              {details.estimated_facility_reimbursement && 
                <Text mb={1}>Facility Reimbursement: ${details.estimated_facility_reimbursement.toFixed(2)}</Text>
              }
              {details.estimated_non_facility_reimbursement && 
                <Text mb={1}>Non-Facility Reimbursement: ${details.estimated_non_facility_reimbursement.toFixed(2)}</Text>
              }
              
              <Text fontWeight="bold" mt={3} mb={1}>Justification:</Text>
              <Box 
                pl={2}
                fontSize="sm"
                whiteSpace="pre-line"
                dangerouslySetInnerHTML={{
                  __html: details.justification ? details.justification.replace(/\n/g, '<br/>') : 'No justification provided'
                }}
              />
            </Box>
            
            {index !== array.length - 1 && <Divider my={4} />}
          </Box>
          );
        })}
        
        {/* 5. Raw JSON Data (last) */}
        <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50" overflowX="auto">
          <Heading size="sm" mb={2}>Raw JSON Data</Heading>
          <div 
            dangerouslySetInnerHTML={{ 
              __html: `<pre style="white-space: pre-wrap; font-size: 0.875rem;">${JSON.stringify(results, null, 2).replace(/\\n/g, '<br/>')}</pre>` 
            }} 
          />
        </Box>
      </Box>
    );
  };

  const formatIcd10Results = (results) => {
    if (!results) return null;
  
    const icd10Codes = results.icd10_codes || results;

    const getConfidenceBadge = (confidence) => {
      if (confidence >= 85) return 'green';
      if (confidence >= 50) return 'yellow';
      return 'red';
    };

    // Filter codes based on confidence score
    const filteredCodes = Object.entries(icd10Codes).filter(([code, details]) => {
      // Ensure we only process valid code objects that have a confidence score >= 40
      return typeof details === 'object' && details !== null && details.hasOwnProperty('confidence') && details.confidence >= 40;
    });
    
    return (
      <Box>
        {results.coder_notes && (
          <Box mb={5} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
            <Heading size="md" mb={3}>Coder Notes</Heading>
            <Box className="markdown-content">
              <ReactMarkdown>{results.coder_notes}</ReactMarkdown>
            </Box>
          </Box>
        )}
        
        {filteredCodes.map(([code, details], index, array) => {
          return (
            <Box key={code} mb={5}>
              <Heading size="md" mb={1}>{code}</Heading>
              <Text fontWeight="bold" mb={3}>{details.description}</Text>
              
              <Box pl={4} mb={3}>
                <Text mb={1}>Condition: {details.condition || "Not specified"}</Text>
                <Text mb={1}>Status: {details.status || "Not specified"}</Text>
                
                <HStack spacing={2} mt={2} mb={2}>
                  {details.is_primary && <Badge colorScheme="green">Primary Diagnosis</Badge>}
                  {details.supports_medical_necessity && 
                    <Badge colorScheme="blue">
                      Supports Medical Necessity
                    </Badge>
                  }
                  <Badge colorScheme={getConfidenceBadge(details.confidence)}>
                    Confidence: {details.confidence}
                  </Badge>
                </HStack>
                
                <Text fontWeight="bold" mt={3} mb={1}>Evidence:</Text>
                <Box 
                  pl={2}
                  fontSize="sm"
                  whiteSpace="pre-line"
                  dangerouslySetInnerHTML={{
                    __html: details.evidence ? details.evidence.replace(/\n/g, '<br/>') : 'No evidence provided'
                  }}
                />
                
                <Text fontWeight="bold" mt={3} mb={1}>Justification:</Text>
                <Box 
                  pl={2}
                  fontSize="sm"
                  whiteSpace="pre-line"
                  dangerouslySetInnerHTML={{
                    __html: details.justification ? details.justification.replace(/\n/g, '<br/>') : 'No justification provided'
                  }}
                />
                
                {details.coverage_note && (
                  <>
                    <Text fontWeight="bold" mt={3} mb={1}>Coverage Note:</Text>
                    <Box 
                      pl={2}
                      fontSize="sm"
                      whiteSpace="pre-line"
                    >
                      {details.coverage_note}
                    </Box>
                  </>
                )}
              </Box>
              
              {index !== array.length - 1 && <Divider my={4} />}
            </Box>
          );
        })}
        
        <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50" overflowX="auto">
          <Heading size="sm" mb={2}>Raw JSON Data</Heading>
          <div 
            dangerouslySetInnerHTML={{ 
              __html: `<pre style="white-space: pre-wrap; font-size: 0.875rem;">${JSON.stringify(results, null, 2).replace(/\\n/g, '<br/>')}</pre>` 
            }} 
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box fontSize="xl">
      <Grid
        templateAreas={`"header header"
                        "main main"
                        "footer footer"`}
        gridTemplateRows={'60px 1fr 60px'}
        gridTemplateColumns={'1fr'}
        h='100%'
        gap='2'
        color='blackAlpha.800'
      >
        <HeaderMenus />
  
        <GridItem bg='white' padding={4} area={'main'}>
          <Flex>
            {/* Left Column - Case List */}
            <Box width="280px" pr={4} borderRightWidth="1px">
              <Button 
                leftIcon={<AddIcon />} 
                colorScheme="green" 
                variant="solid" 
                size="md" 
                width="100%" 
                mb={4}
                onClick={handleNewCase}
                isDisabled={!currentCaseId}
              >
                New Case
              </Button>

              <Box mb={4}>
                <HStack>
                  <Input
                    placeholder="Search by External Case ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    size="sm"
                  />
                  <Button
                    size="sm"
                    onClick={handleSearch}
                    isLoading={casesLoading}
                  >
                    Search
                  </Button>
                </HStack>
                {activeSearch && (
                  <Button size="xs" variant="link" onClick={handleClearSearch} mt={2} colorScheme="blue">
                    Clear Search Results
                  </Button>
                )}
              </Box>

              <Heading size="md" mb={4}>Cases</Heading>

              <VStack spacing={3} align="stretch" maxH="calc(100vh - 250px)" overflowY="auto">
                {cases.length > 0 ? (
                  <>
                    {cases.map(caseItem => (
                      <Box 
                        key={caseItem.id}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        bg={currentCaseId === caseItem.id ? "green.50" : "white"}
                        borderColor={currentCaseId === caseItem.id ? "green.400" : "gray.200"}
                        cursor="pointer"
                        onClick={() => selectCase(caseItem.id)}
                        _hover={{ 
                          bg: currentCaseId === caseItem.id ? "green.100" : "gray.50",
                          borderColor: currentCaseId === caseItem.id ? "green.500" : "gray.300"
                        }}
                        transition="all 0.2s"
                      >
                        <HStack mb={1}>
                          {caseItem.cpt_status === 'in_progress' && <Spinner size="xs" color="orange.500" />}
                          {caseItem.cpt_status === 'complete' && <Icon as={CheckIcon} color="green.500" />}
                          
                          <Text fontWeight="bold" fontSize="sm">
                            {caseItem.case_description || "No description"}
                          </Text>
                        </HStack>
                        
                        <Text fontSize="xs" color="gray.600" mb={2}>
                          {formatDateShort(caseItem.date_updated)} - {caseItem.external_case_id}
                        </Text>
                        
                        <HStack spacing={2} mt={1} flexWrap="wrap">
                          {caseItem.cpt_status && caseItem.cpt_status !== 'not_started' && (
                            <Badge 
                              colorScheme={caseItem.cpt_status === 'complete' ? "green" : 
                                          caseItem.cpt_status === 'in_progress' ? "orange" : "gray"} 
                              fontSize="xs"
                            >
                              CPT: {caseItem.cpt_status}
                            </Badge>
                          )}
                          {caseItem.icd10_status && caseItem.icd10_status !== 'not_started' && (
                            <Badge 
                              colorScheme={caseItem.icd10_status === 'complete' ? "purple" : 
                                          caseItem.icd10_status === 'in_progress' ? "orange" : "gray"} 
                              fontSize="xs"
                            >
                              ICD-10: {caseItem.icd10_status}
                            </Badge>
                          )}
                          <Badge colorScheme="blue" fontSize="xs">{caseItem.case_type}</Badge>
                          <Badge colorScheme="blue" fontSize="xs">{getFileCount(caseItem)} files</Badge>
                        </HStack>
                      </Box>
                    ))}
                    
                    {paginationInfo.hasMore && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={loadMoreCases} 
                        isLoading={casesLoading}
                        width="100%"
                        mt={2}
                      >
                        Load More Cases
                      </Button>
                    )}
                  </>
                ) : (
                  <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <Text color="gray.500" fontSize="sm" textAlign="center">
                      No cases yet. Add content to create your first case.
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
    
            {/* Middle Column - Content Entry and Results */}
            <Box flex="1" pl={4} pr={4}>
              <VStack spacing={6} align="stretch">
                {/* Content Entry Section */}
                <Card variant="outline" borderColor="teal.200">
                  <CardHeader p={3} bg="teal.50" onClick={() => setShowContentEntry(!showContentEntry)} cursor="pointer">
                    <Flex justify="space-between" align="center">
                      <Text fontWeight="bold" fontSize="md">
                        {!currentCaseId ? "Create Case: Paste or Upload Here" : "Add Content to Current Case"}
                      </Text>
                      <IconButton
                        icon={showContentEntry ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        variant="ghost"
                        aria-label={showContentEntry ? "Collapse" : "Expand"}
                        size="sm"
                      />
                    </Flex>
                  </CardHeader>
                  
                  <Collapse in={showContentEntry} animateOpacity>
                    <CardBody>
                      <Box width="100%" mb={4}>
                        <Textarea
                          placeholder="Enter note or any other documentation here..."
                          value={textContent}
                          onChange={(e) => setTextContent(e.target.value)}
                          rows={6}
                          mb={3}
                        />
                        <HStack spacing={3}>
                          {!currentCaseId ? (
                            <Button
                              variant="outline"
                              colorScheme="teal"
                              onClick={handleTextSubmit}
                              isLoading={loading}
                              size="md"
                            >
                              Submit and Create Case
                            </Button>
                          ) : (
                            <Button
                              leftIcon={<AddIcon />}
                              colorScheme="teal"
                              onClick={handleTextSubmit}
                              isLoading={loading}
                            >
                              Add to Current Case
                            </Button>
                          )}
                        </HStack>
                      </Box>

                      <Divider my={4} />

                      <Box width="100%">
                        <Text mb={3}>Or upload pdf, txt, png, or jpg files:</Text>
                        <HStack spacing={3}>
                          {!currentCaseId ? (
                            <Button
                              variant="outline"
                              colorScheme="blue"
                              onClick={handleFileUpload}
                              isLoading={loading}
                            >
                              Upload and Create Case
                            </Button>
                          ) : (
                            <Button
                              leftIcon={<AttachmentIcon />}
                              colorScheme="blue"
                              onClick={handleFileUpload}
                              isLoading={loading}
                            >
                              Add to Current Case
                            </Button>
                          )}
                        </HStack>
                      </Box>
                    </CardBody>
                  </Collapse>
                </Card>

                {/* Status Section */}
                {currentCaseId && (
                  <Box borderWidth="1px" borderRadius="lg" p={8} bg="white" shadow="sm" textAlign="center">
                    <VStack spacing={4}>
                      {workflowStatus?.step1_baseline_coding?.completed && 
                       workflowStatus?.step2_carrier_assignment?.completed && 
                       workflowStatus?.step3_carrier_adjustments?.completed ? (
                        <>
                          <Heading size="md" color="green.600">Medical Necessity Analysis Complete</Heading>
                          <Text color="gray.600">
                            The case has been fully coded and analyzed.
                          </Text>
                          <Button
                            size="lg"
                            colorScheme="green"
                            variant="solid"
                            leftIcon={<ViewIcon />}
                            onClick={handleGeneratePdf}
                            isLoading={isPdfGenerating}
                            loadingText="Generating PDF..."
                          >
                            View Medical Necessity PDF
                          </Button>
                        </>
                      ) : (
                        <>
                          <Heading size="md" color="brand.700">Case Created, Coding Queued</Heading>
                          <Text color="gray.600">
                            Please add more documentation above, or create a new case.
                          </Text>
                        </>
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Box>
            {/* Right Column - Case Details */}
            <Box width="350px" pl={4} borderLeftWidth="1px">
              <Card variant="outline" height="100%">
                <CardHeader p={3} bg="blue.50">
                  <Text fontWeight="bold" fontSize="md">Case Details</Text>
                </CardHeader>
                <CardBody>
                  {caseData ? (
                    <>
                      {/* Links removed per user request */}

                      {/* Case Info */}
                      {caseData.status && (
                        <Badge 
                          colorScheme={
                            caseData.status.toLowerCase() === 'complete' 
                              ? "green" 
                              : caseData.status.toLowerCase() === 'in_progress' 
                                ? "orange" 
                                : "gray"
                          } 
                          mb={2}
                        >
                          {caseData.status}
                        </Badge>
                      )}
                      <Text><strong>Case ID:</strong> {caseData.external_case_id}</Text>
                      <Text><strong>Type:</strong> {caseData.case_type}</Text>
                      {caseData.client && (
                        <Text><strong>Coding Configuration:</strong> {caseData.client.name} ({caseData.client.client_type})</Text>
                      )}
                      <Text><strong>Created:</strong> {formatDate(caseData.date_created)}</Text>

                      <VStack spacing={2} align="stretch" mt={4}>
                        <Button
                          as="a"
                          href={`/case-admission-status/${caseData.external_case_id}`}
                          target="_blank"
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          leftIcon={<ExternalLinkIcon />}
                        >
                          Admission Status Analysis
                        </Button>
            
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          leftIcon={<DeleteIcon />}
                          onClick={() => handleArchiveCase(currentCaseId)}
                        >
                          Archive Case
                        </Button>
                      </VStack>

                      {/* Files Section */}
                      {sortedFiles.length > 0 && (
                        <Box mt={4}>
                          <Heading size="sm" mb={4}>Uploaded Content</Heading>
                          <VStack spacing={2} align="stretch">
                            {sortedFiles.map(file => (
                              <VStack
                                key={file.id}
                                p={3}
                                borderWidth="1px"
                                borderRadius="md"
                                align="stretch"
                                spacing={2}
                                _hover={{ bg: 'gray.50' }}
                                transition="background-color 0.2s"
                              >
                                <Box
                                  onClick={() => handleFileClick(file)}
                                  cursor="pointer"
                                  color="blue.600"
                                  fontSize="sm"
                                  fontWeight="medium"
                                  textDecoration="underline"
                                >
                                  <Text 
                                    whiteSpace="normal" 
                                    wordBreak="break-word" 
                                    noOfLines={3}
                                  >
                                    {file.file_name}
                                  </Text>
                                </Box>
                                <Flex justify="space-between" align="center">
                                  <HStack spacing={3}>
                                    <Badge 
                                      colorScheme={getCategoryColor(file.document_category)} 
                                      fontSize="xs"
                                    >
                                      {file.document_category || 'General'}
                                    </Badge>
                                    <Text fontSize="xs" color="gray.500">
                                      {formatFileSize(file.file_size)}
                                    </Text>
                                  </HStack>
                                  <IconButton
                                    icon={<DownloadIcon />}
                                    size="xs"
                                    aria-label="Download file"
                                    title="Download file"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFileDownload(file);
                                    }}
                                    isDisabled={loading}
                                    minW="auto"
                                    h="auto"
                                    p={1}
                                  />
                                </Flex>
                              </VStack>
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Text color="gray.500">Select or create a case to view details</Text>
                  )}
                </CardBody>
              </Card>
            </Box>
          </Flex>
        </GridItem>
      </Grid>
  
      {/* File Content Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>
            {selectedFile?.file_name}
            <Tag size="sm" colorScheme="blue" ml={2} mt={-1}>
              <TagLabel>{selectedFile?.file_type}</TagLabel>
            </Tag>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg="gray.50" 
              maxHeight="70vh" 
              overflowY="auto"
              whiteSpace="pre-wrap"
              fontFamily="monospace"
            >
              {selectedFile?.file_text || "No content available"}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Text fontSize="sm" color="gray.500" mr="auto">
              {selectedFile ? formatFileSize(selectedFile.file_size) : ""} • 
              {selectedFile ? " Uploaded " + formatDate(selectedFile.upload_date) : ""}
            </Text>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default PilotUploader;